import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
} from '@mui/material'
import {
  Add as AddIcon,
  Link as LinkIcon,
  InsertDriveFile as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  MenuBook as MenuBookIcon,
  Send as SendIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { getFileIcon, getFileIconColor } from '../../../../utils/fileUtils'
import SettingsPanel from './SettingsPanel'

/**
 * 合并的源文件与状态面板
 * 5 步：1上传文件 2生成大纲 3选择题目 4生成正文进度 5笔记本
 */
const SourceAndStatusPanel = ({
  sources,
  selectedCourseCode,
  contentStyle,
  onAddSource,
  onRemoveSource,
  onFileUpload,
  onAddUrl,
  onCloseMenu,
  anchorEl,
  onContentStyleChange,
  messages,
  isUploading,
  notebookData,
  onConfirmGenerate,
  onGenerateNotebook,
  onAdjustOutline,
  isAdjustingOutline,
  generationProgress,
  uploadProgress,
  uploadResult,
  messagesEndRef,
  navigate,
  generateInlineExamples = true,
  onGenerateInlineExamplesChange,
  inlineExampleTypes,
  onInlineExampleTypesChange,
  onOutlineItemClick,
  onSectionClick,
}) => {
  const { t } = useTranslation()
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [adjustOutlineInput, setAdjustOutlineInput] = useState('')
  const scrollContainerRef = useRef(null)

  const hasPassedOutlinePhase = ['generating_sections', 'sections_completed', 'generating_exercises', 'exercises_completed', 'completed'].includes(generationProgress?.stage)
  const outlineReady = notebookData?.outline?.length > 0 && (!notebookData?.sections || notebookData.sections.length === 0) && !isUploading && !hasPassedOutlinePhase
  const canGenerateNotebook = sources.length > 0 && !!selectedCourseCode && !isUploading
  const hasFileSource = sources.some((s) => s.file)
  const canSendAdjust = outlineReady && onAdjustOutline && adjustOutlineInput.trim() && !isAdjustingOutline
  const sectionsCompleted = generationProgress?.stage === 'sections_completed' || (notebookData?.sections?.length > 0 && !isUploading && hasPassedOutlinePhase)

  const displayOutline = notebookData?.outline || []
  const getStreamingOutline = () => {
    if (generationProgress?.data?.partial_outline?.sections) {
      return generationProgress.data.partial_outline.sections.map((item, i) => ({
        title: item.title || `Section ${i + 1}`,
        description: item.description || '',
        order: item.order || i + 1,
        marker: item.marker || '',
        source_content: item.source_content || '',
      }))
    }
    if (generationProgress?.data?.outline?.sections) {
      const s = generationProgress.data.outline.sections
      return Array.isArray(s) ? s.map((item, i) => ({
        title: item.title || `Section ${i + 1}`,
        description: item.description || '',
        order: item.order || i + 1,
        marker: item.marker || '',
        source_content: item.source_content || '',
      })) : null
    }
    return null
  }
  const outlineForDisplay = getStreamingOutline() || displayOutline

  const steps = [
    { id: 'upload', label: t('uploadNotebook.stepUpload') || '上传文件' },
    { id: 'outline', label: t('uploadNotebook.stepOutline') || '生成大纲' },
    { id: 'confirm', label: t('uploadNotebook.stepConfirm') || '选择题目' },
    { id: 'progress', label: t('uploadNotebook.stepProgress') || '生成正文' },
    { id: 'notebook', label: t('uploadNotebook.stepNotebook') || '笔记本' },
  ]
  const totalSteps = steps.length

  const computeActiveStep = () => {
    if (sectionsCompleted && notebookData?.notebookId) return 4
    if (isUploading && generationProgress?.stage === 'generating_sections') return 3
    if (outlineReady) return 2
    if (messages.length > 0 || generationProgress?.stage === 'outline_completed') return 1
    return 0
  }
  const targetStep = computeActiveStep()
  const activeStepIndex = Math.min(Math.max(0, currentStepIndex), totalSteps - 1)
  // 下一步按钮：仅当已到达该步骤时才可点击（未到达的步骤不显示/禁用）
  const canGoNext = activeStepIndex < targetStep
  const canGoPrev = activeStepIndex > 0
  // 已到达的步骤数（分母）：未到达的步骤不显示在进度中
  const reachedStepsCount = targetStep + 1

  useEffect(() => {
    if (targetStep !== activeStepIndex) setCurrentStepIndex(targetStep)
  }, [targetStep, activeStepIndex])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (el) el.scrollTop = 0
  }, [activeStepIndex])

  const getNotebookImage = (title) => `https://picsum.photos/seed/${encodeURIComponent(title || 'notebook')}/400/200`

  const handleConfirmOutline = () => setCurrentStepIndex(2)

  const handleOutlineItemClick = (item) => {
    onOutlineItemClick?.(item)
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', flex: 1, minHeight: 0 }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{steps[activeStepIndex]?.label}</Typography>
          <Typography variant="caption" color="text.secondary">({activeStepIndex + 1} / {reachedStepsCount})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton size="small" onClick={() => setCurrentStepIndex((i) => Math.max(0, i - 1))} disabled={!canGoPrev} aria-label="上一步">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          {canGoNext && (
            <IconButton size="small" onClick={() => setCurrentStepIndex((i) => Math.min(totalSteps - 1, i + 1))} aria-label="下一步">
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      <Box ref={scrollContainerRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', p: 2, WebkitOverflowScrolling: 'touch' }}>
        {/* 步骤1：上传文件 */}
        {activeStepIndex === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {sources.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center', color: 'text.secondary' }}>
                <FileIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 500 }}>{t('uploadNotebook.noSources') || '暂无源文件'}</Typography>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.6 }}>{t('uploadNotebook.addSourceHint') || '点击下方按钮添加文件或 URL'}</Typography>
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                {sources.map((source) => (
                  <Card key={source.id} sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ color: getFileIconColor(source.type) || 'primary.main', mt: 0.5 }}>{React.createElement(getFileIcon(source.type))}</Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.5 }}>{source.name}</Typography>
                          <Chip label={source.type === 'url' ? (t('uploadNotebook.web') || '网页') : source.type.toUpperCase()} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        </Box>
                        <IconButton size="small" onClick={() => onRemoveSource(source.id)} sx={{ ml: 'auto' }}><DeleteIcon fontSize="small" /></IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}
            <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <SettingsPanel contentStyle={contentStyle} onContentStyleChange={onContentStyleChange} />
              <Button variant="contained" fullWidth startIcon={<AddIcon />} onClick={onAddSource} sx={{ textTransform: 'none', mb: 2, bgcolor: 'text.primary', color: 'background.paper', '&:hover': { bgcolor: 'text.primary', opacity: 0.9 } }}>
                {t('uploadNotebook.addSource') || '添加源文件'}
              </Button>
              <Button variant="contained" color="primary" fullWidth startIcon={<MenuBookIcon />} onClick={onGenerateNotebook} disabled={!canGenerateNotebook || !hasFileSource} sx={{ textTransform: 'none' }}>
                {t('uploadNotebook.generateNotebook') || '生成笔记'}
              </Button>
              {sources.length === 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('uploadNotebook.addSourceFirst') || '请先添加源文件'}</Typography>}
              {sources.length > 0 && !selectedCourseCode && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{t('uploadNotebook.selectCourseFirst') || '请先选择课程'}</Typography>}
            </Box>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onCloseMenu}>
              <MenuItem onClick={onAddUrl}><LinkIcon sx={{ mr: 1 }} />{t('uploadNotebook.addUrl') || '添加 URL'}</MenuItem>
              <MenuItem component="label"><FileIcon sx={{ mr: 1 }} />{t('uploadNotebook.uploadFile') || '上传文件'}<input type="file" hidden multiple accept=".pdf,.doc,.docx,.ppt,.pptx,.md,.markdown,.json,.txt" onChange={onFileUpload} /></MenuItem>
            </Menu>
          </Box>
        )}

        {/* 步骤2：生成大纲 */}
        {activeStepIndex === 1 && (
          <Box>
            {messages.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('uploadNotebook.noMessages') || '暂无消息'}</Typography>
                <Typography variant="caption" color="text.secondary">{t('uploadNotebook.goToStep1') || '请先在上一步添加文件并点击生成笔记'}</Typography>
              </Box>
            ) : (
              <>
                {/* 安装程序式状态栏：只显示当前状态 */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    mb: 2,
                    borderRadius: 1,
                    bgcolor: outlineForDisplay.length > 0 ? 'success.light' : 'action.hover',
                    border: '1px solid',
                    borderColor: outlineForDisplay.length > 0 ? 'success.main' : 'divider',
                  }}
                >
                  {outlineForDisplay.length > 0 ? (
                    <CheckCircleIcon sx={{ fontSize: 22, color: 'success.main', flexShrink: 0 }} />
                  ) : (
                    <CircularProgress size={20} sx={{ flexShrink: 0 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 500, color: outlineForDisplay.length > 0 ? 'success.dark' : 'text.primary' }}>
                    {outlineForDisplay.length > 0
                      ? (t('uploadNotebook.outlineCompletedStatus') || '✓ 大纲生成完成。请点击下方章节预览，确认后点击「确认大纲」。')
                      : (t('uploadNotebook.generatingOutline') || '正在生成大纲...')}
                  </Typography>
                </Box>
                {uploadProgress !== undefined && uploadProgress > 0 && outlineForDisplay.length === 0 && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{uploadProgress}%</Typography>
                  </Box>
                )}
                {outlineForDisplay.length > 0 && (
                  <Card sx={{ mt: 2, border: '1px solid', borderColor: 'divider' }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>{t('uploadNotebook.outline') || '大纲'}（共 {outlineForDisplay.length} 章）</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('uploadNotebook.clickOutlineToPreview') || '点击章节可在右侧预览'}</Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {outlineForDisplay.sort((a, b) => (a.order || 0) - (b.order || 0)).map((item, idx) => (
                          <Box
                            key={item.title || idx}
                            onClick={() => handleOutlineItemClick(item)}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{item.order || idx + 1}. {item.title}</Typography>
                            {item.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{item.description}</Typography>}
                          </Box>
                        ))}
                      </Box>
                      {onAdjustOutline && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('uploadNotebook.adjustOutlineHint') || '对大纲不满意？输入修改意图：'}</Typography>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField fullWidth multiline minRows={1} maxRows={3} placeholder={t('uploadNotebook.adjustOutlinePlaceholder') || '例如：合并第2、3节；增加一节「小结」'} value={adjustOutlineInput} onChange={(e) => setAdjustOutlineInput(e.target.value)} disabled={isAdjustingOutline} size="small" />
                            <Tooltip title={t('uploadNotebook.sendAdjustOutline') || '发送并调整大纲'}>
                              <span>
                                <IconButton color="primary" onClick={() => { const text = adjustOutlineInput.trim(); if (text) { onAdjustOutline(text); setAdjustOutlineInput('') } }} disabled={!canSendAdjust} size="small" sx={{ flexShrink: 0 }}><SendIcon fontSize="small" /></IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>
                      )}
                      <Button variant="contained" color="primary" fullWidth startIcon={<CheckCircleIcon />} onClick={handleConfirmOutline} sx={{ mt: 2, textTransform: 'none', py: 1.5 }}>
                        {t('uploadNotebook.confirmOutline') || '确认大纲'}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {messagesEndRef && <div ref={messagesEndRef} />}
              </>
            )}
          </Box>
        )}

        {/* 步骤3：选择题目 + 生成正文 */}
        {activeStepIndex === 2 && outlineReady && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('uploadNotebook.stepConfirmDesc') || '选择要生成的题目类型，然后点击生成正文'}</Typography>
            {onGenerateInlineExamplesChange && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel control={<Checkbox size="small" checked={generateInlineExamples !== false} onChange={(e) => onGenerateInlineExamplesChange(e.target.checked)} />} label={<Typography variant="body2" color="text.secondary">{t('uploadNotebook.generateInlineExamples') || '笔记是否附带例子'}</Typography>} />
                <Collapse in={generateInlineExamples !== false}>
                  <Box sx={{ pl: 3.5, mt: 0.5, pt: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                    {inlineExampleTypes && onInlineExampleTypesChange && (
                      <FormGroup sx={{ flexDirection: 'column', gap: 0 }}>
                        {[
                          { key: 'multiple_choice', label: t('uploadNotebook.multipleChoice') || '选择题' },
                          { key: 'code', label: t('uploadNotebook.code') || '代码题' },
                          { key: 'code_tracing', label: t('uploadNotebook.codeTracing') || '代码追踪题' },
                          { key: 'proof', label: t('uploadNotebook.proof') || '证明题' },
                          { key: 'short_answer', label: t('uploadNotebook.shortAnswer') || '简答题' },
                        ].map(({ key, label }) => (
                          <Box
                            key={key}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.75,
                              borderRadius: 1,
                              cursor: 'pointer',
                              transition: 'background-color 0.2s',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <FormControlLabel
                              control={<Checkbox size="small" checked={!!inlineExampleTypes[key]} onChange={(e) => onInlineExampleTypesChange({ ...inlineExampleTypes, [key]: e.target.checked })} />}
                              label={<Typography variant="body2">{label}</Typography>}
                              sx={{ m: 0, flex: 1 }}
                            />
                          </Box>
                        ))}
                      </FormGroup>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}
            {onConfirmGenerate && (
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button variant="contained" color="primary" fullWidth startIcon={<MenuBookIcon />} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConfirmGenerate() }} disabled={isUploading} size="medium" sx={{ textTransform: 'none', py: 1.5, fontWeight: 600 }}>
                  {t('uploadNotebook.generateContent') || '生成正文'}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* 步骤4：生成正文进度 */}
        {activeStepIndex === 3 && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                px: 2,
                mb: 2,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <CircularProgress size={20} sx={{ flexShrink: 0 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {t('uploadNotebook.generatingSections') || '正在生成章节...'}
              </Typography>
            </Box>
            {uploadProgress !== undefined && uploadProgress > 0 && (
              <>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{uploadProgress}%</Typography>
              </>
            )}
            {generationProgress?.stage === 'generating_sections' && (generationProgress?.data?.logs?.length > 0) && (() => {
              const logs = generationProgress.data.logs
              const stepRegex = /^\[(\d+)\/(\d+)\]\s*(.+)$/
              const byStep = {}
              let maxTotal = 0
              logs.forEach((line) => {
                const m = line.match(stepRegex)
                if (m) {
                  const step = parseInt(m[1], 10)
                  const total = parseInt(m[2], 10)
                  maxTotal = Math.max(maxTotal, total)
                  if (!byStep[step]) byStep[step] = { total, lines: [] }
                  byStep[step].lines.push(m[3].trim())
                }
              })
              const total = maxTotal || 1
              const outlineTitles = notebookData?.outline?.map((o) => o.title) || []
              const checklistItems = []
              for (let step = 1; step <= total; step++) {
                const { lines = [] } = byStep[step] || {}
                const isSuccess = lines.some((l) => /✓|创建完成/.test(l))
                const isError = lines.some((l) => /✗|创建失败/.test(l))
                const hasStarted = lines.length > 0
                const titleMatch = lines.find((l) => /正在创建章节:/.test(l))?.match(/正在创建章节:\s*(.+)/) || lines.find((l) => /章节\s*['"]/.test(l))?.match(/章节\s*['"](.+?)['"]\s*创建/) || lines.find((l) => /创建失败/.test(l))?.match(/章节\s*['"](.+?)['"]\s*创建失败/)
                const title = titleMatch ? titleMatch[1].trim() : (lines[0]?.match(stepRegex)?.[3]?.trim() || outlineTitles[step - 1] || `章节 ${step}`)
                const errorMsg = isError && lines.find((l) => /创建失败:/.test(l)) ? lines.find((l) => /创建失败:/.test(l))?.replace(/^✗\s*章节\s*['"].+?['"]\s*创建失败:\s*/, '') : null
                const status = isError ? 'error' : isSuccess ? 'completed' : hasStarted ? 'in_progress' : 'pending'
                checklistItems.push({ step, total, title, status, errorMsg })
              }
              const getCheckIcon = (item) => item.status === 'completed' ? CheckCircleIcon : item.status === 'error' ? ErrorIcon : item.status === 'in_progress' ? HourglassEmptyIcon : RadioButtonUncheckedIcon
              const getIconColor = (item) => item.status === 'completed' ? 'success.main' : item.status === 'error' ? 'error.main' : item.status === 'in_progress' ? 'primary.main' : 'action.disabled'
              return (
                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', maxHeight: 280, overflowY: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>{t('uploadNotebook.sectionProgress') || '生成进度'}</Typography>
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {checklistItems.map((item, idx) => {
                      const IconComp = getCheckIcon(item)
                      return (
                        <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, px: 1, borderRadius: 1, bgcolor: item.status === 'error' ? 'error.light' : item.status === 'completed' ? 'success.light' : item.status === 'in_progress' ? 'primary.light' : 'transparent', border: '1px solid', borderColor: item.status === 'error' ? 'error.main' : item.status === 'completed' ? 'success.main' : item.status === 'in_progress' ? 'primary.main' : 'transparent', opacity: item.status === 'pending' ? 0.6 : 1 }}>
                          <IconComp sx={{ fontSize: 20, flexShrink: 0, mt: 0.15, color: getIconColor(item) }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: item.status === 'pending' ? 400 : 600, lineHeight: 1.4, color: item.status === 'pending' ? 'text.secondary' : 'text.primary' }}>{item.step}. {item.title}</Typography>
                            {item.errorMsg && <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.25 }}>{item.errorMsg}</Typography>}
                          </Box>
                        </Box>
                      )
                    })}
                  </Box>
                </Box>
              )
            })()}
          </Box>
        )}

        {/* 步骤5：笔记本 */}
        {activeStepIndex === 4 && notebookData?.notebookId && (
          <Box>
            <Card onClick={() => navigate(`/notebook/${notebookData.notebookId}`)} sx={{ mb: 2, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
              <CardMedia sx={{ height: 80 }} image={getNotebookImage(notebookData.fullOutline?.notebook_title || notebookData.description)} title={notebookData.fullOutline?.notebook_title || ''} />
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>{notebookData.fullOutline?.notebook_title || notebookData.description?.split('。')[0] || (t('uploadNotebook.newNotebook') || '新笔记')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('uploadNotebook.clickChapterToPreview') || '点击章节可在右侧预览内容'}</Typography>
              </CardContent>
            </Card>
            {notebookData?.sections?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>{t('uploadNotebook.chapters') || '章节'}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{t('uploadNotebook.clickChapterToPreview') || '点击章节可在右侧预览内容'}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {notebookData.sections.map((section, index) => {
                    const outlineItem = notebookData.outline?.find((o) => o.title === section.section_title) || { title: section.section_title, order: index + 1 }
                    return (
                      <Box
                        key={section.section_title || index}
                        onClick={() => onSectionClick?.(section, outlineItem)}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{outlineItem.order || index + 1}. {section.section_title}</Typography>
                      </Box>
                    )
                  })}
                </Box>
              </Box>
            )}
            {uploadResult && (
              <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mt: 2 }}>
                {uploadResult.message}
                {uploadResult.success && uploadResult.notebook && navigate && <Button size="small" onClick={() => navigate(`/notebook/${uploadResult.notebook.id}`)} sx={{ ml: 2 }}>{t('uploadNotebook.viewNotebook') || '查看笔记'}</Button>}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default SourceAndStatusPanel
